import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { buttonClass } from "@/components/buttonStyles";

// The page-level safety net (BRK-S032): each shell wraps its <Outlet /> in this,
// so a render throw in any page swaps just the page for a branded apology while
// the shell's header, sidebar and footer stay mounted, instead of React
// unmounting the whole app to a white screen (CLAUDE.md #7). It is the generic
// ErrorBoundary (BRK-S259) with a page-shaped fallback; logging is that
// boundary's console.error, and nothing else (no telemetry).
//
// The location key is a reset key, so any navigation (the sidebar, Back, the
// fallback's own home link) gives the next page a fresh try. A router 404 never
// reaches it: the `*` route renders NotFoundPage, which doesn't throw.
export function PageErrorBoundary({
  children,
  homeTo,
  homeLabel,
}: {
  children: ReactNode;
  /** Where the escape hatch leads: the shell's home. */
  homeTo: string;
  homeLabel: string;
}) {
  const { key } = useLocation();
  return (
    <ErrorBoundary
      resetKeys={[key]}
      fallback={({ reset }) => (
        <PageErrorFallback
          onRetry={reset}
          homeTo={homeTo}
          homeLabel={homeLabel}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

// Laid out like NotFoundPage so the two "this page can't be shown" screens read
// as one family. Mobile-first: a centered single column whose actions stack on
// a phone and sit side by side from sm.
function PageErrorFallback({
  onRetry,
  homeTo,
  homeLabel,
}: {
  onRetry: () => void;
  homeTo: string;
  homeLabel: string;
}) {
  return (
    <section
      role="alert"
      className="flex flex-col items-center gap-4 py-16 text-center"
    >
      <h1 className="text-title">Something went wrong</h1>
      <p className="max-w-prose text-brk-dim">
        Sorry, this page couldn’t be shown. Try again, or head back and pick up
        from there.
      </p>
      <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
        <button type="button" onClick={onRetry} className={buttonClass()}>
          Try again
        </button>
        <Link to={homeTo} className={buttonClass({ variant: "secondary" })}>
          {homeLabel}
        </Link>
      </div>
    </section>
  );
}
